import { dispatchGallery } from '@/pages/gallery/gallery.store'
import { dispatchMenuEntries } from '@/pages/menuEntries/menuEntries.store'
import { type MenuElement } from '@/pages/menuEntries/MenuEntry.type'
import {
  type PageContent,
  type PageElement,
  sortCarouselGuestIds,
} from '@/pages/menuEntries/pageSetup/pageContent.type'

import { type RootState } from '../../store'
import { exhaustiveCheck } from '../../utils'
import { createStore, type GettersObj } from '../../utils/createStore'
import { ApplicationDataService } from '../service/applicationDataService'
import { ErrorService } from '../service/errorService'
import { MobilePreviewFallbackFonts } from '../service/fontsService'
import { colorService } from '../service/utils.service'
import type {
  DeeplinkElementId,
  ImageId,
  PreviewDeeplinkElement,
  PreviewGrabData,
  PreviewNavBarContent,
  PreviewPageContent,
  PreviewPageElement,
} from '../types/applicationData.type'

// Gallery payloads are hydrated to `GalleryItem[]` before they reach the
// store (server-side for the grab; in parse/setElementData for the editor
// endpoints), so the adapter is a pure pass-through.

type DeeplinkBase = Pick<
  PreviewDeeplinkElement,
  | 'id'
  | 'title'
  | 'icon'
  | 'privateSectionSecret'
  | 'colorText'
  | 'colorIcon'
  | 'fontSize'
  | 'fontFamily'
>

function buildDeeplinkBase(el: MenuElement | PageElement): DeeplinkBase {
  return {
    id: el.id as DeeplinkElementId,
    title: el.titles.fr,
    // Editor stores icons as plain strings; mobile's IconProp accepts them
    // as part of its IconName union.
    icon: el.icon as DeeplinkBase['icon'],
    privateSectionSecret: el.privateSectionSecret,
    colorText: el.colorText,
    colorIcon: el.colorIcon,
    fontSize: el.fontSize,
    fontFamily: el.fontFamily,
  }
}

// Returns undefined for VIDEOS / SPLASH / PROGRAM_TAGS_FILTER (backoffice-only
// variants with no mobile counterpart).
function adaptDeeplink<B extends DeeplinkBase>(
  el: MenuElement | PageElement,
  base: B,
): (B & PreviewDeeplinkElement) | undefined {
  switch (el.type) {
    case 'HOME':
    case 'MENU':
    case 'LIKES':
    case 'NOTIFICATIONS':
    case 'SPONSOR_LIST':
    case 'ABOUT':
    case 'EMPTY':
    case 'CHAT':
    case 'MY_TICKETS':
      return { ...base, type: el.type }
    case 'PROGRAM':
      return {
        ...base,
        type: 'PROGRAM',
        programIds: el.programIds,
        defaultSegment: el.defaultSegment,
      }
    case 'PRACTICAL_INFO':
      return {
        ...base,
        type: 'PRACTICAL_INFO',
        infoPratiqueId: el.infoPratiqueId,
      }
    case 'PRACTICAL_INFO_LIST':
      return {
        ...base,
        type: 'PRACTICAL_INFO_LIST',
        infoPratiqueIds: el.infoPratiqueIds,
      }
    case 'GUEST_LIST':
      return { ...base, type: 'GUEST_LIST', groupIds: el.groupIds }
    case 'GUEST_LIST_TYPE':
      return { ...base, type: 'GUEST_LIST_TYPE', typeIds: el.typeIds }
    case 'MAP':
      return { ...base, type: 'MAP', mapIds: el.mapIds }
    case 'WEBVIEW':
      return {
        ...base,
        type: 'WEBVIEW',
        url: el.url,
        openInWebview: el.openInWebview,
      }
    case 'TICKETING':
      return { ...base, type: 'TICKETING', url: el.url }
    case 'CASHLESS':
      return { ...base, type: 'CASHLESS', url: el.url }
    case 'WEEZEVENT':
      return { ...base, type: 'WEEZEVENT', url: el.url }
    case 'GALLERY':
      return { ...base, type: 'GALLERY', galleryData: el.galleryData }
    case 'GUEST':
      return { ...base, type: 'GUEST', groupId: el.groupId }
    case 'EVENT':
      return { ...base, type: 'EVENT', eventId: el.eventId }
    case 'SAFER':
      return { ...base, type: 'SAFER', apiKey: el.apiKey }
    case 'NEWS_LIST':
      return { ...base, type: 'NEWS_LIST' }
    case 'VIDEOS':
    case 'SPLASH':
    case 'PROGRAM_TAGS_FILTER':
      return undefined
    default:
      exhaustiveCheck(el)
      return undefined
  }
}

export type GettersAppData = GettersObj<typeof mapGettersAppData>

export class AppDataState {
  data: PreviewGrabData | null = null
  modifiedHomeContents: PreviewPageContent[] | null = null
  modifiedMenuContents: PreviewPageContent[] | null = null
}

function adaptMenuElement(
  el?: MenuElement,
): PreviewDeeplinkElement | undefined {
  if (!el) return undefined
  return adaptDeeplink(el, buildDeeplinkBase(el))
}

function adaptPageElement(el: PageElement): PreviewPageElement | undefined {
  // `image` / `a11yHint` are common to every PageElement variant, so they
  // ride on the shared base passed into the type switch.
  const base = {
    ...buildDeeplinkBase(el),
    a11yHint: el.a11yHint?.fr,
    image: el.image?.fr as ImageId | undefined,
  }
  return adaptDeeplink(el, base)
}

function adaptElementSlot(el?: PageElement): PreviewPageElement | undefined {
  if (!el || !el.isVisible) return undefined
  return adaptPageElement(el)
}

function adaptPageContent(
  serverPageContent: PageContent,
  rootState: RootState,
): PreviewPageContent {
  // HOME_CUSTOM_* element slots must be COLLAPSED, not left as `undefined`:
  // mobile's page-contents.tsx reads `.type` on each tuple entry and
  // crashes on `[undefined]` (e.g. a freshly-added content with no element
  // yet). The tuple types allow 0- or 1-tuples, so drop missing slots.
  switch (serverPageContent.type) {
    case 'HOME_CUSTOM_FULL': {
      const e = adaptElementSlot(serverPageContent.elements[0])
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_CUSTOM_FULL',
        elements: e ? [e] : [],
        title: serverPageContent.title?.fr,
        heightFactor: serverPageContent.heightFactor,
      }
    }
    case 'HOME_CUSTOM_HALF': {
      const e0 = adaptElementSlot(serverPageContent.elements[0])
      const e1 = adaptElementSlot(serverPageContent.elements[1])
      const elements: [PreviewPageElement?, PreviewPageElement?] =
        e0 && e1 ? [e0, e1] : e0 ? [e0] : e1 ? [e1] : []
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_CUSTOM_HALF',
        elements,
        title: serverPageContent.title?.fr,
        heightFactor: serverPageContent.heightFactor,
      }
    }
    case 'HOME_CUSTOM_THIRD': {
      const elements: [
        PreviewPageElement?,
        PreviewPageElement?,
        PreviewPageElement?,
      ] = []
      for (const slot of serverPageContent.elements) {
        const adapted = adaptElementSlot(slot)
        if (adapted) elements.push(adapted)
      }
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_CUSTOM_THIRD',
        elements,
        title: serverPageContent.title?.fr,
        heightFactor: serverPageContent.heightFactor,
      }
    }
    case 'PAGE_LIST':
      return {
        id: serverPageContent.id ?? '',
        type: 'PAGE_LIST',
        elements: serverPageContent.elements
          .filter((e): e is PageElement => !!e && e.isVisible)
          .map((e) => adaptPageElement(e))
          .filter((e): e is PreviewPageElement => !!e),
        title: serverPageContent.title?.fr,
      }
    case 'HOME_PROGRAMS':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_PROGRAMS',
        programIds: serverPageContent.programIds ?? [],
        maxRows: serverPageContent.maxRows,
        defaultSegment: serverPageContent.defaultSegment,
        title: serverPageContent.title?.fr ?? '',
        orderByEndHour: serverPageContent.orderByEndHour,
      }
    case 'HOME_LIVE':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_LIVE',
        programIds: serverPageContent.programIds,
        title: serverPageContent.title?.fr ?? '',
      }
    case 'HOME_AD':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_AD',
        title: serverPageContent.title?.fr,
      }
    case 'HOME_SOCIAL_NETWORKS':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_SOCIAL_NETWORKS',
        title: serverPageContent.title?.fr,
      }
    case 'HOME_YOUTUBE':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_YOUTUBE',
        youtubeId: serverPageContent.youtubeId,
        title: serverPageContent.title?.fr,
        autoPlay: serverPageContent.autoPlay ?? false,
      }
    case 'HOME_EVENT':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_EVENT',
        eventId: serverPageContent.eventId,
        title: serverPageContent.title?.fr,
      }
    case 'HOME_GUEST':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_GUEST',
        guestId: serverPageContent.guestId,
        title: serverPageContent.title?.fr,
      }
    case 'HOME_COVER_FLOW_GUEST': {
      const isProgram = serverPageContent.mode === 'program'
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_COVER_FLOW_GUEST',
        mode: serverPageContent.mode,
        guestIds: isProgram
          ? (serverPageContent.guestIds ?? [])
          : sortCarouselGuestIds(
              serverPageContent.guestIds ?? [],
              rootState.guests.guests,
            ),
        eventIds: serverPageContent.eventIds,
        title: serverPageContent.title?.fr,
        autoPlay: serverPageContent.autoPlay ?? false,
        autoPlayInterval: serverPageContent.autoPlayInterval ?? 2500,
      }
    }
    case 'HOME_IMAGE': {
      const { mainImageTemp, ratioTemp } = rootState.pageSetup
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_IMAGE',
        image: mainImageTemp
          ? { previewDataUri: mainImageTemp }
          : serverPageContent.image,
        videoFilename: serverPageContent.videoFilename,
        ratio: ratioTemp ?? serverPageContent.ratio ?? 4 / 3,
      }
    }
    case 'HOME_SUB_IMAGE': {
      const { subImageTemp, subImageRatioTemp } = rootState.pageSetup
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_SUB_IMAGE',
        image: subImageTemp
          ? { previewDataUri: subImageTemp }
          : serverPageContent.image,
        ratio: subImageRatioTemp ?? serverPageContent.ratio ?? 7.5,
        isVisible: serverPageContent.isVisible,
      }
    }
    case 'HOME_COUNTDOWN':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_COUNTDOWN',
        dateParam: serverPageContent.dateParam,
        title: serverPageContent.title?.fr,
      }
    case 'HOME_NOTIFICATIONS_CAROUSEL':
      return {
        id: serverPageContent.id ?? '',
        type: 'HOME_NOTIFICATIONS_CAROUSEL',
        maxNotifications: serverPageContent.maxNotifications ?? 10,
        maxAgeDays: serverPageContent.maxAgeDays ?? 7,
        title: serverPageContent.title?.fr,
      }
    default:
      exhaustiveCheck(serverPageContent)
      throw new Error('Unhandled PageContent variant')
  }
}

export const {
  appData,
  commit: commitAppData,
  dispatch: dispatchAppData,
  mapGetters: mapGettersAppData,
  mapState: mapStateAppData,
  useGetter: useGetterAppData,
  useState: useStateAppData,
} = createStore({
  namespaced: true,
  moduleName: 'appData',
  initState: new AppDataState(),
  mutations: {
    SET_INITIAL_STATE(state: AppDataState, initialGrab: PreviewGrabData) {
      state.data = initialGrab
    },
    SET_MODIFIED_CONTENTS(
      state: AppDataState,
      payload: {
        page: 'HOME_PAGE' | 'MENU_PAGE'
        contents: PreviewPageContent[]
      },
    ) {
      if (payload.page === 'HOME_PAGE') {
        state.modifiedHomeContents = payload.contents
      } else {
        state.modifiedMenuContents = payload.contents
      }
    },
  },
  getters: {
    getGrabData: (state, getters, rootState): PreviewGrabData | null => {
      if (!state.data) return null

      const newNavBar = rootState.menuEntries.navBar
      const newPageContents = rootState.pageSetup.pageContents
      const { currentPage } = rootState.pageSetup
      const currentAppConfig = rootState.appConfig.appConfig

      // Compute current unsaved changes if we are on a setup page
      let currentUnsavedHome: PreviewPageContent[] | null =
        state.modifiedHomeContents
      let currentUnsavedMenu: PreviewPageContent[] | null =
        state.modifiedMenuContents

      if (newPageContents && newPageContents.length) {
        const contents = newPageContents
          .filter((val) => val.isVisible)
          .map((val) => adaptPageContent(val as PageContent, rootState))

        if (contents.length) {
          // Can't commit from a getter; watchers / navigation persist this
          // back to the session.
          if (currentPage === 'HOME_PAGE') {
            currentUnsavedHome = contents
          } else if (currentPage === 'MENU_PAGE') {
            currentUnsavedMenu = contents
          }
        }
      }

      const adaptedState: PreviewGrabData = {
        ...state.data,
        applicationConfig: {
          ...state.data.applicationConfig,
          previewImageOverrides: {
            ic_launcher: {
              previewDataUri: rootState.storesInformations.appStore
                ?.applicationIcone
                ? `/api/files/${rootState.storesInformations.appStore.applicationIcone}`
                : '/images/chapito_logo.png',
            },
            'chapito_logo.png': {
              previewDataUri: '/images/chapito_logo.png',
            },
            'codetroopers_logo.png': {
              previewDataUri: '/images/codetroopers_logo.png',
            },
          },
          ...(currentAppConfig
            ? {
                colorAction: colorService.rgba_to_argb(
                  currentAppConfig.colorAction,
                ),
                colorBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorBackground,
                ),
                colorBlockBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorBlockBackground,
                ),
                colorBlockText: colorService.rgba_to_argb(
                  currentAppConfig.colorBlockText,
                ),
                colorTitle: colorService.rgba_to_argb(
                  currentAppConfig.colorTitle,
                ),
                colorOverrideNavbarIcon: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideNavbarIcon,
                ),
                colorOverrideBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideBackground,
                ),
                colorOverrideHeaderAccent: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideHeaderAccent,
                ),
                colorOverrideNavbarBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideNavbarBackground,
                ),
                colorOverrideHeaderBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideHeaderBackground,
                ),
                colorOverrideNavbarTextUnselected: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideNavbarTextUnselected,
                ),
                colorOverrideTitle: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideTitle,
                ),
                colorOverrideTimetableHours: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideTimetableHours,
                ),
                colorOverrideBlocIcons: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideBlocIcons,
                ),
                colorOverrideCountdown: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideCountdown,
                ),
                colorOverrideBlockBackground: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideBlockBackground,
                ),
                colorOverrideNavbarBlock: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideNavbarBlock,
                ),
                colorOverrideBlockText: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideBlockText,
                ),
                colorOverrideNavbarTextSelected: colorService.rgba_to_argb(
                  currentAppConfig.colorOverrideNavbarTextSelected,
                ),
                showBlockShadows: !!currentAppConfig.showBlockShadows,
                enableVibrations: !!currentAppConfig.enableVibrations,
              }
            : {}),
          fontTitle:
            currentAppConfig?.fontTitle ??
            state.data.applicationConfig.fontTitle ??
            MobilePreviewFallbackFonts.fontTitle.fontName,
          fontTextBold:
            currentAppConfig?.fontTextBold ??
            state.data.applicationConfig.fontTextBold ??
            MobilePreviewFallbackFonts.fontTextBold.fontName,
          fontTextBoldItalic:
            currentAppConfig?.fontTextBoldItalic ??
            state.data.applicationConfig.fontTextBoldItalic ??
            MobilePreviewFallbackFonts.fontTextBoldItalic.fontName,
          fontTextRegular:
            currentAppConfig?.fontTextRegular ??
            state.data.applicationConfig.fontTextRegular ??
            MobilePreviewFallbackFonts.fontTextRegular.fontName,
          fontTextRegularItalic:
            currentAppConfig?.fontTextRegularItalic ??
            state.data.applicationConfig.fontTextRegularItalic ??
            MobilePreviewFallbackFonts.fontTextRegularItalic.fontName,
          fontTextSemiBold:
            currentAppConfig?.fontTextSemiBold ??
            state.data.applicationConfig.fontTextSemiBold ??
            MobilePreviewFallbackFonts.fontTextSemiBold.fontName,
        },
      }
      if (newNavBar) {
        adaptedState.navBar = {
          ...newNavBar,
          elements: newNavBar.elements
            .filter((val): val is MenuElement => !!val && val.isVisible)
            .map(adaptMenuElement) as PreviewNavBarContent['elements'],
        }
      }

      // Inject session-level modifications or current active edits
      if (currentUnsavedHome) {
        adaptedState.homeContents = currentUnsavedHome
      }
      if (currentUnsavedMenu) {
        adaptedState.menuContents = currentUnsavedMenu
      }

      return adaptedState
    },
  },
  actions: {
    async loadCurrentData(
      { commit },
      payload: { filterOnlyVisible: boolean },
    ): Promise<void> {
      try {
        // Gallery must be loaded before `getAllContent` runs — the menu/page
        // parsers need it to hydrate GALLERY payloads.
        const [initialGrab] = await Promise.all([
          ApplicationDataService.getCurrent('fr', payload.filterOnlyVisible),
          dispatchGallery('getGalleryItems'),
        ])
        commit('SET_INITIAL_STATE', initialGrab)
        await dispatchMenuEntries('getAllContent')
      } catch (e) {
        ErrorService.handleError(e, false)
      }
    },
  },
})
